/* eslint-disable */
import { debounce } from 'lodash-es'
import { witnessWallet } from '~/services/profile'
import { connectAccount, disconnectAccount, getWeb3, getProvider, signMessage } from '~/services/account'
import { getAccountData, getValueData } from '~/services/prices'
import { EBO_CONTRACT_ADDR } from "~/utils/constants";
import { getBalance } from "~/services/ethereum";

// const LAST_SYNCED_WALLET = 'LAST_SYNCED_WALLET'

let provider = null
let web3Instance = null

export const state = () => ({
  windowWidth: window.innerWidth,
  bgColor: '#fff',
  textColor: '#000',
  secondaryColor: '#818182',
  walletAddress: null,
  showSyncOverlay: false,
  portfolioValue: null,
  portfolioChange24Hr: null,
  loadingMessageOverlay: null,
  syncPending: false,
  canMint: false,
})

export const getters = {
  provider: () => provider,
  web3Instance: () => web3Instance,
  bgColor: (state) => state.bgColor,
  textColor: (state) => state.textColor,
  secondaryColor: (state) => state.secondaryColor,
  portfolioValue: (state) => state.portfolioValue,
  portfolioChange24Hr: (state) => state.portfolioChange24Hr,
  showSyncOverlay: (state) => state.showSyncOverlay,
  synced: (state) => !!state.walletAddress,
  syncPending: (state) => state.syncPending,
  walletAddress: (state) => state.walletAddress,
  canMint: (state) => !!state.canMint,
}

export const mutations = {
  setColorPrefs(state, { bg, text, secondary }) {
    if (bg) state.bgColor = bg
    if (text) state.textColor = text
    if (secondary) state.secondaryColor = secondary
  },
  setWalletAddress(state, address) {
    state.walletAddress = address
  },
  setSyncPending(state, pending) {
    state.syncPending = !!pending
  },
  setWindowWidth(state) {
    state.windowWidth = window.innerWidth
  },
  setTransactions(state, transactions) {
    state.transactions = transactions
  },
  setSyncOverlay(state, show) {
    state.showSyncOverlay = !!show
  },
  setLoadingMessageOverlay(state, message) {
    state.loadingMessageOverlay = message
  },
  setPortfolioData(state, { value, change24Hr }) {
    state.portfolioValue = value
    state.portfolioChange24Hr = change24Hr
  },
  setCanMint(state, canMint) {
    state.canMint = canMint;
  },
}

const debouncedUpdateColorPrefs = debounce((state) => {
  localStorage.setItem(
    'colorPrefs',
    JSON.stringify({
      bg: state.bgColor,
      text: state.textColor,
      secondary: state.secondaryColor,
    })
  )
}, 100)

export const actions = {
  updateColorPrefs({ commit, state }, colorPrefs) {
    if (!colorPrefs) return
      ;['bg', 'text', 'secondary'].forEach((color) => {
        const hexRegex = /^#[A-Fa-f0-9]{6}$/
        if (color in colorPrefs && !hexRegex.test(colorPrefs[color])) {
          throw new Error(`Invalid color hex: ${colorPrefs[color]}`)
        }
      })
    commit('setColorPrefs', colorPrefs)
    requestAnimationFrame(() => {
      document.documentElement.style.setProperty('--bgcolor', state.bgColor)
      document.documentElement.style.setProperty('--textcolor', state.textColor)
      document.documentElement.style.setProperty('--secondarycolor', state.secondaryColor)
    })
    debouncedUpdateColorPrefs(state)
  },

  async initWallet({ dispatch, commit }, showModal = true) {
    // initWallet is invoked on page load. Prevent syncing wallet in no accounts are cached
    if (!showModal && !localStorage.getItem('-CBWSDK:SCWStateManager:accounts')) return
    
    commit('setSyncPending', true)

    try {
      await dispatch('updateWalletAddress', null)
      const account = await connectAccount()
      provider = getProvider()
      web3Instance = getWeb3()

      if (!account) throw `Please connect to a Web3 wallet`

      // No need to await here
      dispatch('updateWalletAddress', account)
      dispatch('wallet/updateBalances', null, { root: true })
    } catch (error) {
      dispatch('unsync')
      this.$toast.show(error)
      throw error
    } finally {
      commit('setSyncPending', false)
    }
  },

  async updateWalletAddress({ commit, dispatch }, walletAddress) {
    console.log('connected', walletAddress)
    commit('setWalletAddress', walletAddress)
    if (walletAddress) {
      this.$toast.show(`Connected to account ${walletAddress}`)
      witnessWallet(walletAddress).catch(console.error)
      await Promise.all([
        dispatch('currentUserProfile/getProfile'),
        dispatch('augmintable/connect', { provider, walletAddress }),
        dispatch('updateMintEligibility'),
        getAccountData(walletAddress, 0)
          .then(({ tokens }) => getValueData(tokens, walletAddress))
          .then((valueData) => commit('setPortfolioData', valueData)),
      ])
    }
  },

  async updateMintEligibility({ commit, state }) {
    if (!state.walletAddress) return commit("setCanMint", false);
    // const eboTokenBalance = await getBalance(EBO_CONTRACT_ADDR, state.walletAddress);
    // console.info({ eboTokenBalance });
    // commit("setCanMint", eboTokenBalance >= 100);
    commit("setCanMint", true);
  },

  async unsync({ commit, dispatch }) {
    await disconnectAccount()
    await dispatch('updateWalletAddress', null)
    await dispatch('augmintable/disconnect')
    dispatch('currentUserProfile/clearProfile')
    commit('setPortfolioData', { value: null, change24Hr: null })
    this.$toast.show(`Disconnected from wallet`)
  },

  signMessage(_, message) {
    return signMessage(message)
  },
}

let store // eslint-disable-line import/no-mutable-exports
if (process.client) {
  window.onNuxtReady(({ $store, $device }) => {
    try {
      const colorPrefs = JSON.parse(localStorage.getItem('colorPrefs'))
      if (colorPrefs) $store.dispatch('updateColorPrefs', colorPrefs)
    } catch (e) {
      console.warn('Problem using stored color prefs', e)
    }

    if (!$device.isMobile && !$store.state.syncPending) {
      $store.commit('setSyncOverlay', true)
    }

    store = $store
  })
}

export const mapFrameArgs = (argNames) => {
  const frameArgs = {}

  argNames.forEach((argName) => {
    frameArgs[argName] = {
      get() {
        return store.getters['create/selectedFrames'][0].args[argName]
      },
      set(val) {
        store.dispatch('create/updateSelected', {
          args: {
            [argName]: val,
          },
        })
      },
    }
  })
  return frameArgs
}
export { store as default }
